/**
 * Module dependencies.
 */

import { Badge } from 'src/components/core/badges/badge';
import { Button } from 'src/components/core/buttons/button';
import { CardGradient } from 'src/components/core/cards/card-gradient';
import { DiscountModal } from './discount-modal';
import { Loading } from 'src/components/core/loading';
import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import { axiosInstance } from 'src/core/utils/requests';
import { routes } from 'src/core/constants/routes';
import { transparentize } from 'src/styles/utils/colors';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCallback, useEffect, useId, useState } from 'react';
import { useGTMEvent } from 'src/hooks/use-gtm-event';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useRouter } from 'next/router';
import { useSchedule } from 'src/providers/schedule';
import { useTranslation } from 'next-i18next';
import checkSolidSvg from 'src/assets/svgs/checked-solid.svg';
import scanBateriaSvg from 'src/assets/svgs/electric.svg';
import styled from 'styled-components';
import verificar360Svg from 'src/assets/svgs/verificar360.svg';

/**
 * `Service` type.
 */

type Service = {
  DiscountRate: number;
  OriginalPriceInclVat: number;
  PriceInclVat: number;
  TypeDescription: string;
  TypeID: number;
  TypeName: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled(CardGradient)`
  background: var(--gradient-secondary--45-deg);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 450px;
  min-height: 170px;
  padding: var(--space-xs) var(--space-xxs) var(--space-xxs);
  position: relative;
`;

/**
 * `StyledButton` styled component.
 */

export const StyledButton = styled(Button)`
  > span {
    justify-content: center;
  }
`;

/**
 * `PointsBetweenButtons` styled component.
 */

const PointsBetweenButtons = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-bottom: var(--space-xxs);
  margin-top: var(--space-xxs);
`;

/**
 * `PointsGradientText` styled component — same gradient as rewards star icon.
 */

const PointsGradientText = styled.span`
  background: linear-gradient(to top, #00a7dc 0%, #00cddc 48.5%, #01dccf 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`;

/**
 * `ServicesList` styled component.
 */

const ServicesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xxs);
  margin-bottom: var(--space-xxs);
`;

/**
 * `ServiceHeader` styled component.
 */

const ServiceHeader = styled.div`
  display: flex;
  gap: var(--space-xxs);
  justify-content: space-between;
`;

/**
 * `ServiceTitleWrapper` styled component.
 */

const ServiceTitleWrapper = styled.div`
  align-items: start;
  display: flex;
  gap: var(--space-xxs);
  margin-bottom: 6px;
`;

/**
 * `ServiceTitle` styled component.
 */

const ServiceTitle = styled(Text)`
  background: var(--gradient-primary--15-deg);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: var(--color-primary);
`;

/**
 * `PriceWrapper` styled component.
 */

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * `Price` styled component.
 */

const Price = styled.div`
  align-items: center;
  display: flex;
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg)`
  color: var(--color-primary);
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: opacity var(--transition-fast);
`;

/**
 * `RadioWrapper` styled component.
 */

const RadioWrapper = styled.div`
  border: 2px solid var(--radio-border-color);
  border-radius: var(--border-radius-xl);
  height: var(--radio-size);
  position: relative;
  transition: border-color var(--transition-fast);
  width: var(--radio-size);
`;

/**
 * `ServiceCard` styled component.
 */

const ServiceCard = styled.label`
  --radio-border-color: var(--color-slate300);
  --radio-icon-color: var(--color-primary);
  --radio-size: 24px;

  background-color: ${transparentize('cyan800', 0.3)};
  border: 2px solid transparent;
  border-radius: var(--border-radius-xs);
  cursor: pointer;
  display: grid;
  gap: var(--space-xs);
  grid-template-columns: 1fr max-content;
  padding: 12px;
  position: relative;
  transition: var(--transition-default);
  transition-property: background-color, border-color, color;
  width: 100%;

  ${Icon} {
    opacity: 0;
  }

  input:checked ~ ${RadioWrapper} {
    --radio-border-color: var(--color-primary);

    ${Icon} {
      opacity: 1;
    }
  }

  :focus,
  :focus-within,
  :hover {
    border-color: var(--color-primary);
  }

  &[data-active='true'] {
    border-color: var(--color-primary);
  }
`;

/**
 * `ServicePrice` component.
 */

const ServicePrice = ({ currentPrice, originalPrice }: { currentPrice: number; originalPrice: number }) => {
  return (
    <PriceWrapper>
      <Price>
        <Text as={'p'} style={{ color: 'var(--color-text)' }} variant={'titleL'} weight={600}>
          {currentPrice}
        </Text>
        <Text as={'sup'} style={{ color: 'var(--color-text)' }} variant={'titleM'} weight={600}>
          {'€'}
        </Text>
      </Price>

      {originalPrice && originalPrice !== currentPrice && (
        <Price>
          <Text
            as={'p'}
            style={{ color: 'var(--color-text-subtle)', textDecoration: 'line-through' }}
            variant={'titleM'}
            weight={600}
          >
            {originalPrice}
          </Text>
          <Text as={'sup'} style={{ color: 'var(--color-text-subtle)' }} variant={'titleXs'} weight={600}>
            {'€'}
          </Text>
        </Price>
      )}
    </PriceWrapper>
  );
};

/**
 * Export `ServicePayment` component.
 */

export const ServicePayment = () => {
  const pointsStarGradientId = `payment-points-star-${useId().replace(/:/g, '')}`;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const sendGTMEvent = useGTMEvent();
  const { t } = useTranslation('schedule');

  const { isSantanderFlow, processCode, setStep } = useSchedule();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [services, setServices] = useState<Service[]>([]);
  const [currentService, setCurrentService] = useState<Service | null>(null);

  const [isDiscountModalOpen, setDiscountModalOpen] = useState(false);
  const router = useRouter();

  const isMobile = useBreakpoint(0, 390);

  const paymentGetServices = useCallback(async () => {
    try {
      await axiosInstance
        .post('/payment-get-services', { isSantanderFlow })
        .then(({ data }) => {
          const services = data?.data?.services as Service[];

          setServices(services);
        })
        .catch(() => {
          setStep(7);
        });
    } catch (error) {
      setStep(7);
    }
  }, [isSantanderFlow, setStep]);

  useEffect(() => {
    paymentGetServices();
  }, [processCode, paymentGetServices]);

  const paymentGenerateLink = useCallback(async () => {
    setIsSubmitting(true);

    const token = await executeRecaptcha?.('schedulePaymentGenerateLink');

    try {
      axiosInstance
        .post('/payment-generate-link', {
          amount: currentService?.PriceInclVat,
          processCode,
          token,
          typeId: currentService?.TypeID
        })
        .then(({ data }) => {
          const paymentLink = data?.data?.paymentLink;

          sendGTMEvent({
            event: 'payment_generate_link',
            process_id: processCode // eslint-disable-line id-match
          });

          setIsSubmitting(false);

          if (paymentLink) {
            setCurrentService(null);

            window.open(paymentLink, '_blank');
          }
        })
        .catch(() => {
          setIsSubmitting(false);
          setStep(7);
        });
    } catch (error) {
      setIsSubmitting(false);
      setStep(7);
    }
  }, [currentService?.PriceInclVat, currentService?.TypeID, executeRecaptcha, processCode, sendGTMEvent, setStep]);

  const sendPayLaterMessage = useCallback(async () => {
    setIsSubmitting(true);

    try {
      await axiosInstance
        .post('/send-message', {
          messageText: `O cliente selecionou o serviço '${currentService?.TypeName}' e optou por pagar no próprio dia.`,
          messageTitle: `Tipo de Serviço: ${currentService?.TypeName}`,
          processCode
        })
        .then(() => {
          setIsSubmitting(false);

          router.push(isSantanderFlow ? routes.landingPage : '/');
        })
        .catch(() => {
          setIsSubmitting(false);
          setStep(7);
        });
    } catch (error) {
      setIsSubmitting(false);
      setStep(7);
    }
  }, [currentService?.TypeName, isSantanderFlow, processCode, router, setStep]);

  const handlePayNow = () => {
    setDiscountModalOpen(false);
    paymentGenerateLink();
  };

  const handlePayLater = () => {
    setDiscountModalOpen(true);
    sendPayLaterMessage();
  };

  return (
    <Wrapper>
      {services.length === 0 && <Loading />}

      {services.length > 0 && (
        <>
          <ServicesList>
            {services.map((service, index) => (
              <ServiceCard
                data-active={currentService === service}
                key={index}
                onClick={() => setCurrentService(service)}
              >
                <ServiceHeader>
                  <div style={{ maxWidth: '70%' }}>
                    <ServiceTitleWrapper>
                      {(service.TypeID === 3 || service.TypeID === 25) && (
                        <Svg icon={service.TypeID === 3 ? verificar360Svg : scanBateriaSvg} size={'24px'} />
                      )}

                      <ServiceTitle variant={'labelM'} weight={600}>
                        {service.TypeName}
                      </ServiceTitle>

                      {service.DiscountRate > 0 && (
                        <Badge>{t('payment.service.discount', { discount: service.DiscountRate })}</Badge>
                      )}
                    </ServiceTitleWrapper>

                    {service.TypeDescription && (
                      <Text style={{ color: 'var(--color-text)' }} variant={'bodyXs'}>
                        {service.TypeDescription}
                      </Text>
                    )}
                  </div>

                  <ServicePrice currentPrice={service.PriceInclVat} originalPrice={service.OriginalPriceInclVat} />
                </ServiceHeader>

                <div>
                  <input name={'Sevices'} style={{ display: 'none' }} type={'radio'} />

                  <RadioWrapper>
                    <Icon icon={checkSolidSvg} size={'24px'} />
                  </RadioWrapper>
                </div>
              </ServiceCard>
            ))}
          </ServicesList>

          {isSantanderFlow ? (
            <>
              <StyledButton
                aria-label={t('payment.actions.santander.payLater')}
                disabled={currentService === null || isSubmitting}
                isLoading={isSubmitting}
                onClick={sendPayLaterMessage}
                reverse
                size={isMobile ? 'small' : 'medium'}
                stretch
              >
                {t('payment.actions.santander.payLater')}
              </StyledButton>

              <PointsBetweenButtons data-name={'payment-points-row'}>
                <svg
                  aria-hidden
                  fill={'none'}
                  height={'12'}
                  viewBox={'0 0 12 12'}
                  width={'12'}
                  xmlns={'http://www.w3.org/2000/svg'}
                >
                  <defs>
                    <linearGradient
                      gradientUnits={'userSpaceOnUse'}
                      id={pointsStarGradientId}
                      x1={'5.99984'}
                      x2={'5.99984'}
                      y1={'10.9974'}
                      y2={'0.999573'}
                    >
                      <stop stopColor={'#00A7DC'} />
                      <stop offset={'0.485'} stopColor={'#00CDDC'} />
                      <stop offset={'1'} stopColor={'#01DCCF'} />
                    </linearGradient>
                  </defs>

                  <path
                    d={
                      'M5.30435 1.46898C5.55684 0.843104 6.44293 0.843104 6.69542 1.46898L7.66406 3.87004C7.69944 3.95775 7.7813 4.01799 7.87557 4.02568L10.3086 4.22421C10.9683 4.27804 11.24 5.09775 10.743 5.53489L8.89672 7.15873C8.82839 7.21882 8.79811 7.3113 8.81766 7.40016L9.40805 10.0837C9.55553 10.754 8.79969 11.2539 8.24056 10.8558L6.14489 9.36366C6.05809 9.30186 5.94167 9.30186 5.85488 9.36366L3.75921 10.8558C3.20008 11.2539 2.44424 10.754 2.59172 10.0837L3.18211 7.40017C3.20166 7.3113 3.17138 7.21882 3.10305 7.15873L1.25673 5.5349C0.7597 5.09777 1.03133 4.27804 1.69105 4.22421L4.1242 4.02568C4.21847 4.01799 4.30033 3.95775 4.33571 3.87004L5.30435 1.46898Z'
                    }
                    fill={`url(#${pointsStarGradientId})`}
                  />
                </svg>

                <PointsGradientText>{t('payment.actions.santander.points')}</PointsGradientText>
              </PointsBetweenButtons>

              <StyledButton
                aria-label={t('payment.actions.santander.payNow')}
                colorTheme={'ghost'}
                disabled={currentService === null || isSubmitting}
                onClick={() => setDiscountModalOpen(true)}
                size={isMobile ? 'small' : 'medium'}
                stretch
              >
                {t('payment.actions.santander.payNow')}
              </StyledButton>
            </>
          ) : (
            <>
              <StyledButton
                aria-label={t('payment.actions.payNow')}
                disabled={currentService === null || isSubmitting}
                isLoading={isSubmitting}
                onClick={paymentGenerateLink}
                reverse
                size={isMobile ? 'small' : 'medium'}
                stretch
              >
                {t('payment.actions.payNow')}
              </StyledButton>

              <StyledButton
                aria-label={t('payment.actions.payLater')}
                colorTheme={'ghost'}
                disabled={currentService === null || isSubmitting}
                onClick={() => setDiscountModalOpen(true)}
                size={isMobile ? 'small' : 'medium'}
                stretch
                style={{
                  marginTop: 'var(--space-xxs)'
                }}
              >
                {t('payment.actions.payLater')}
              </StyledButton>
            </>
          )}

          <DiscountModal
            handlePayLater={handlePayLater}
            handlePayNow={handlePayNow}
            isOpen={isDiscountModalOpen}
            onClose={() => setDiscountModalOpen(false)}
          />
        </>
      )}
    </Wrapper>
  );
};
